class SwiperSlideshow {

  /**
   * Creates a Swiper Slideshow
   * @param {HTMLElement} slideShowElement Wrapper Element for Swiper Container
   */
  constructor(slideShowElement) {
    this.swiperSlideShow = {};
    this.slideShowElement = slideShowElement;

    this.init();
  }

  init() {
    if (!this.slideShowElement.children.length) {
      return;
    }

    if (this.slideShowElement.dataset.jsSwiperInitialized) {
      return;
    }



    this.slideshowConfig = this.slideShowElement.getAttribute('data-js-swiper');
    this.slideShowConfigJSON = this.slideshowConfig ? JSON.parse(this.slideshowConfig) : {};

    this.slideshowConfigReducedMotion = this.slideShowElement.getAttribute('data-js-swiper-reduced-motion');
    this.slideShowConfigReducedMotionJSON = this.slideshowConfigReducedMotion ? JSON.parse(this.slideshowConfigReducedMotion) : {};

    const defaultConfig = {
      init: false,
      autoHeight: false,
      keyboard: true,
      spaceBetween: 16,
      grabCursor: true,
      observer: true,
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
      },
      slideClass: 'slide-show__slide',
      wrapperClass: 'slide-show__wrapper',
    };

    const reducedMotionConfig = {
      autoplay: false,
      autoHeight: false,
      effect: 'fade',
      fadeEffect: {
        crossFade: true
      },
    };

    this.slideShowConfigJSON = Object.assign({}, defaultConfig, this.slideShowConfigJSON);
    this.slideShowConfigReducedMotionJSON = Object.assign({}, reducedMotionConfig, this.slideShowConfigReducedMotionJSON);

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      this.slideShowConfigJSON = Object.assign({}, this.slideShowConfigJSON, this.slideShowConfigReducedMotionJSON);
    }

    // Insert buttons first to get the container
    this.buttonPrevNextContainer = this._insertPrevNextButtons();
    
    // Set navigation to the specific buttons within this container
    if (this.buttonPrevNextContainer) {
      this.slideShowConfigJSON.navigation = {
        nextEl: this.buttonPrevNextContainer.querySelector('[data-js-swiper-next]'),
        prevEl: this.buttonPrevNextContainer.querySelector('[data-js-swiper-prev]'),
      };
    }

    // only init if more than one slide
    if (this.slideShowElement.querySelectorAll(`.${this.slideShowConfigJSON.slideClass}`).length <= 1) {
      this.slideShowElement.dataset.jsSwiperInitialized = 'true';
      return;
    }

    this.swiperSlideShow = new Swiper(this.slideShowElement, this.slideShowConfigJSON);
    this.swiperSlideShow.init();
    
    // Setup resize listener after init to ensure slides are measured
    requestAnimationFrame(() => {
      this.setupResizeListener();
    });
    
    this.slideShowElement.dataset.jsSwiperInitialized = 'true';
  }

  setupResizeListener() {
    // Prevent duplicate observers
    if (this.resizeObserver) {
      return;
    }

    const resizeHandler = () => {
      if (Object.keys(this.swiperSlideShow).length === 0 && this.swiperSlideShow.constructor === Object) {
        // not initialized yet
        return;
      }

      if (this.swiperSlideShow.slides.length === 0) {
        // no slides available
        return;
      }

      const totalSlides = this.swiperSlideShow.slides.length;
      const fullyVisibleSlides = this.getFullyVisibleSlidesCount();

      if (fullyVisibleSlides >= totalSlides) {
        this.disableSwiper();
      } else {
        this.enableSwiper();
      }
    };
    resizeHandler();

    this.resizeObserver = new ResizeObserver(resizeHandler);
    this.resizeObserver.observe(this.slideShowElement);
  }

  getFullyVisibleSlidesCount() {
    // Check if swiper is not in slide mode
    if (this.swiperSlideShow.params?.effect !== 'slide') {
      return this.swiperSlideShow.slides.length > 0 ? 1 : 0;
    }

    const wrapperEl = this.swiperSlideShow.wrapperEl;
    if (!wrapperEl) return 0;

    const wrapperRect = wrapperEl.getBoundingClientRect();
    const epsilon = 0.5; // tolerate sub-pixel rounding
    let count = 0;

    this.swiperSlideShow.slides.forEach(slide => {
      if (!slide || slide.offsetWidth === 0) return;
      const slideRect = slide.getBoundingClientRect();
      const fullyVisible =
        slideRect.left >= wrapperRect.left - epsilon &&
        slideRect.right <= wrapperRect.right + epsilon;

      if (fullyVisible) {
        count += 1;
      }
    });

    return count;
  }

  enableSwiper() {
    this.slideShowElement.dataset.jsSwiperActive = 'true';
    this.swiperSlideShow.enable();

    if (!this.buttonPrevNextContainer) return;
    this.buttonPrevNextContainer.dataset.jsSwiperActive = 'true'; // Container can be placed outside of slideshow
  }

  disableSwiper() {
    this.slideShowElement.dataset.jsSwiperActive = 'false';
    this.swiperSlideShow.disable();

    if (!this.buttonPrevNextContainer) return;
    this.buttonPrevNextContainer.dataset.jsSwiperActive = 'false'; // Container can be placed outside of slideshow
  }

  _insertPrevNextButtons() {
    const buttonPrevNextContainer = this.slideShowConfigJSON.navigationWrapper ? document.querySelector(this.slideShowConfigJSON.navigationWrapper) : this.slideShowElement.querySelector('[data-js-swiper-prev-next]');
    if (!buttonPrevNextContainer) return;

    // SVG path matching Flickity arrow shape
    const arrowPath = 'M 25,50 L 55,80 L 65,75 L 40,50  L 65,25 L 55,20 Z';

    const prevButton = document.createElement('button');
    prevButton.className = 'slide-show__nav-button';
    prevButton.dataset.jsSwiperPrev = '';
    prevButton.innerHTML = `
      <svg viewBox="0 0 100 100">
        <path d="${arrowPath}"></path>
      </svg>
    `;

    const nextButton = document.createElement('button');
    nextButton.className = 'slide-show__nav-button';
    nextButton.dataset.jsSwiperNext = '';
    nextButton.innerHTML = `
      <svg viewBox="0 0 100 100">
        <path d="${arrowPath}" transform="translate(100, 100) rotate(180)"></path>
      </svg>
    `;

    buttonPrevNextContainer.appendChild(prevButton);
    buttonPrevNextContainer.appendChild(nextButton);

    return buttonPrevNextContainer;
  }

  static initSlideShows() {
    const slideShowElements = document.querySelectorAll('[data-js-swiper]');
    slideShowElements.forEach(slideShowElement => {
      if (slideShowElement.dataset.jsSwiperInitialized) {
        return false;
      }

      new SwiperSlideshow(slideShowElement);
    });
  }
}

document.addEventListener('DOMContentLoaded', SwiperSlideshow.initSlideShows);